import { darken, lighten } from 'polished';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';
import COLORS from '../../../constants/COLORS';
import {
  composeValidators,
  phone,
  email,
  required,
  requiredWithMessage,
  minNumberValue,
} from '../../../helpers/validations';
import Button from '../Button';
import Checkbox from '../FormControls/Checkbox';
import Field from '../FormControls/Field';
import Input from '../FormControls/Input';
import NumericInput from '../FormControls/NumericInput';
import PhoneInput from '../FormControls/PhoneInput';
import Textarea from '../FormControls/Textarea';
import OrderSuccessModal from '../OrderSuccessModal';
import Spinner from '../../../shared/components/Spinner';
import useOrderForm from '../../../hooks/useOrderForm';

type OrderFormProps = WithTranslation & {
  termsUrl?: string;
  personalDataTermsUrl?: string;
  isFullscreen: boolean;
};

const OrderForm: React.FC<OrderFormProps> = ({
  termsUrl,
  personalDataTermsUrl,
  isFullscreen,
  t,
}) => {
  const { dates, state, submitForm, setError, updateForm, hideSuccessModal } = useOrderForm();
  const { values, touched, isLoading, showSuccessModal } = state;

  return (
    <Wrapper isFullscreen={isFullscreen} withDates={dates && dates.length > 0}>
      <Form onSubmit={submitForm}>
        <Title isFullscreen={isFullscreen}>{t('orderTickets_marketplace')}</Title>
        <Description>{t('orderTicketsDescription')}</Description>
        <FormRow>
          <Field
            value={values.fio}
            touched={touched}
            validation={required}
            setError={setError}
            name="fio"
          >
            {({ error, name }) => (
              <Input
                label={t('fullName')}
                name={name}
                onChange={updateForm}
                value={values.fio}
                error={error}
              />
            )}
          </Field>
        </FormRow>
        <FormRow>
          <Field
            value={values.email}
            touched={touched}
            validation={composeValidators(required, email)}
            setError={setError}
            name="email"
          >
            {({ error, name }) => (
              <Input
                label={t('email')}
                name={name}
                onChange={updateForm}
                value={values.email}
                error={error}
              />
            )}
          </Field>
        </FormRow>
        <FormRow>
          <Field
            value={values.phone}
            touched={touched}
            validation={composeValidators(required, phone)}
            setError={setError}
            name="phone"
          >
            {({ error, name }) => (
              <PhoneInput
                label={t('phone')}
                id="phone"
                name={name}
                onChange={updateForm}
                value={values.phone}
                error={error}
              />
            )}
          </Field>
        </FormRow>
        <FormRow>
          <Field
            value={values.tickets}
            touched={touched}
            validation={composeValidators(required, minNumberValue(1))}
            setError={setError}
            name="tickets"
          >
            {({ error, name }) => (
              <NumericInput
                label={t('ticketsCount')}
                name={name}
                onChange={updateForm}
                value={values.tickets}
                error={error}
                marketplaceGrey
                max={1000}
              />
            )}
          </Field>
        </FormRow>
        <FormRow>
          <Field
            value={values.comment}
            touched={touched}
            validation={required}
            setError={setError}
            name="comment"
          >
            {({ error, name }) => (
              <Textarea
                label={t('comments')}
                name={name}
                onChange={updateForm}
                value={values.comment}
                error={error}
              />
            )}
          </Field>
        </FormRow>
        <FormRow>
          <Field
            value={values.accept}
            validation={requiredWithMessage(t('notAgreedForProcessingData'))}
            touched={touched}
            setError={setError}
            name="accept"
          >
            {({ error, name }) => (
              <Checkbox
                id="accept"
                name={name}
                onChange={updateForm}
                checked={values.accept}
                error={error}
              >
                {personalDataTermsUrl ? (
                  <>
                    {t('agreeForDataProcessing_1')}{' '}
                    <Link
                      color={COLORS.MARKETPLACE_GREEN}
                      href={personalDataTermsUrl}
                      target="_blank"
                    >
                      {t('agreeForDataProcessing_2')}
                    </Link>{' '}
                    {t('agreeForDataProcessing_3')}
                    {t('agreeForDataProcessing_4')}{' '}
                    <Link color={COLORS.MARKETPLACE_GREEN} href={termsUrl} target="_blank">
                      {t('agreeForDataProcessing_5')}
                    </Link>
                  </>
                ) : (
                  <>
                    {t('agreeForDataProcessing_1')}{' '}
                    <Link color={COLORS.MARKETPLACE_GREEN} href={termsUrl} target="_blank">
                      {t('agreeForDataProcessing_2')}
                    </Link>{' '}
                    {t('agreeForDataProcessing_3')}
                  </>
                )}
              </Checkbox>
            )}
          </Field>
        </FormRow>
        <Button disabled={isLoading} type="submit">
          {isLoading ? <Spinner size={25} isStatic /> : t('submitRequest')}
        </Button>
      </Form>
      {showSuccessModal && <OrderSuccessModal hideSuccessModal={hideSuccessModal} />}
    </Wrapper>
  );
};

// @ts-ignore
export default withTranslation()(OrderForm);

interface WrapperProps {
  isFullscreen: boolean;
  withDates: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  background: ${({ theme }) => theme.backgroundColor || '#f4f4f4'};
  box-sizing: border-box;
  z-index: 11;

  padding-top: ${({ theme: { isLanding, isMobile }, isFullscreen, withDates }) => {
    if (!isFullscreen && !withDates) return null;
    if (withDates && isLanding && isFullscreen && isMobile) return '170px';
    if (withDates && isLanding && isMobile) return '105px';
    if (withDates && isLanding) return '72px';
    if (withDates) return '42px';
    return '65px';
  }};
`;

const Title = styled.div<{ isFullscreen: boolean }>`
  margin-bottom: 0;
  font-weight: bold;
  font-size: 24px;
  line-height: 26px;
  text-align: center;
  margin-top: ${({ isFullscreen }) => {
    if (isFullscreen) return '40px';
  }};
`;

const Description = styled.div`
  font-weight: 500;
  font-size: 14px;
  text-align: center;
  color: rgba(21, 16, 53, 0.4);
  margin-bottom: 20px;
`;

const Form = styled.form`
  max-width: 400px;
  margin: 0 auto;
  padding: 20px 16px;
  box-sizing: border-box;
`;

const FormRow = styled.div`
  margin-bottom: 24px;
`;

const Link = styled.a<{ color: string }>`
  color: ${(props) => props.color || COLORS.BLUE};
  text-decoration: none;

  &:hover {
    color: ${lighten(0.1, COLORS.BLUE)};
  }

  &:focus {
    color: ${darken(0.1, COLORS.BLUE)};
  }
`;
